import { HttpParams } from '@angular/common/http';

import { toHTTPParam } from '../../../../core';
import { Filter } from '../../../../models';
import { isPeriodicidadePeriodo, Periodicidade, periodicidadeMensal } from '../../../../shared';
import { Cidade } from '../../../cidade';
import { Item } from '../../../item';
import { getStatusesConsultaPedidos } from '../../../pedido/status/pedido-status';
import { Pessoa } from '../..//../pessoa';
import { CurvaAbcVendaReferencia, isReferenciaNotaFiscal, isReferenciaPedido, referenciaPedido } from '../referencia';

export class CurvaAbcVendaFilter implements Filter {
    isFilterDirty = false;

    idReferencia: number;
    referencia: CurvaAbcVendaReferencia;

    // Periodicidade Emissão
    periodicidadeEmissao: number;
    dataEmissaoInicial: Date;
    dataEmissaoFinal: Date;
    dataEmissaoInicialPeriodo: Date;
    dataEmissaoFinalPeriodo: Date;
    mesEmissao: number;
    trimestreEmissao: number;
    semestreEmissao: number;
    anoEmissao: number;

    idPessoa: number;
    pessoa: Pessoa;
    idsRepresentantes: number[] = [];
    somenteRepresentantesAtivos = true;
    idsGerentes: number[] = [];
    idsAtendentes: number[] = [];
    idTipoClassificacaoCliente: number;
    idsClassificacoesClientes: number[] = [];
    classificacaoClienteNull = false;
    idTipoClassificacaoItem: number;
    idsClassificacoesItens: number[] = [];
    classificacaoItemNull = false;
    idItem: number;
    item: Item;
    idCidade: number;
    cidade: Cidade;
    idsEstados: number[] = [];
    statusesPedidos: number[] = [];
    idsTagsClientes: number[] = [];
    idsTagsItens: number[] = [];

    constructor(referencia: CurvaAbcVendaReferencia = referenciaPedido, periodicidadeEmissao: Periodicidade = periodicidadeMensal) {
        this.idReferencia = referencia?.id ?? referenciaPedido.id;
        this.referencia = referencia ?? referenciaPedido;
        this.periodicidadeEmissao = periodicidadeEmissao?.id ?? periodicidadeMensal.id;
        this.setDefaults();
    }

    createParams(params: HttpParams): HttpParams {
        if (!params) {
            params = new HttpParams();
        }

        // Referência
        if (this.idReferencia) {
            params = params.append('referencia', this.idReferencia.toString());
        }

        // Periodicidade Emissão
        if (this.periodicidadeEmissao) {
            params = params.append('periodicidadeEmissao', this.periodicidadeEmissao.toString());
        }

        if (isPeriodicidadePeriodo(this.periodicidadeEmissao)) {
            // Data Emissão Inicial
            if (this.dataEmissaoInicialPeriodo) {
                params = params.append('dataEmissaoInicial', toHTTPParam(this.dataEmissaoInicialPeriodo));
            }
            // Data Emissão Final
            if (this.dataEmissaoFinalPeriodo) {
                params = params.append('dataEmissaoFinal', toHTTPParam(this.dataEmissaoFinalPeriodo));
            }
        } else {
            // Data Emissão Inicial
            if (this.dataEmissaoInicial) {
                params = params.append('dataEmissaoInicial', toHTTPParam(this.dataEmissaoInicial));
            }
            // Data Emissão Final
            if (this.dataEmissaoFinal) {
                params = params.append('dataEmissaoFinal', toHTTPParam(this.dataEmissaoFinal));
            }
        }

        // Pessoa
        if (this.idPessoa) {
            params = params.append('idPessoa', this.idPessoa.toString());
        }
        // Representantes
        if (this.idsRepresentantes && this.idsRepresentantes.length > 0) {
            this.idsRepresentantes.forEach(id => {
                params = params.append('idRepresentante', id.toString());
            });
        }

        // Gerentes
        if (this.idsGerentes && this.idsGerentes.length > 0) {
            this.idsGerentes.forEach(id => {
                params = params.append('idsGerentes', id.toString());
            });
        }
        // Atendentes
        if (this.idsAtendentes && this.idsAtendentes.length > 0) {
            this.idsAtendentes.forEach(id => {
                params = params.append('idsAtendentes', id.toString());
            });
        }

        // Tipo Classificação de clientes
        if (this.idTipoClassificacaoCliente) {
            params = params.append('idTipoClassificacaoCliente', this.idTipoClassificacaoCliente.toString());
        }
        // Classificações de clientes
        this.idsClassificacoesClientes?.forEach(idClassificacaoCliente => {
            if (idClassificacaoCliente > 0) {
                params = params.append('idClassificacaoCliente', idClassificacaoCliente.toString());
            }
        });
        // Sem classificação de cliente
        if (this.classificacaoClienteNull) {
            params = params.append('classificacaoClienteNull', 'true');
        }

        // Tipo Classificação de itens
        if (this.idTipoClassificacaoItem) {
            params = params.append('idTipoClassificacaoItem', this.idTipoClassificacaoItem.toString());
        }
        // Classificações de itens
        this.idsClassificacoesItens?.forEach(idClassificacaoItem => {
            if (idClassificacaoItem > 0) {
                params = params.append('idClassificacaoItem', idClassificacaoItem.toString());
            }
        });
        // Sem classificação de item
        if (this.classificacaoItemNull) {
            params = params.append('classificacaoItemNull', 'true');
        }

        // Item
        if (this.idItem) {
            params = params.append('idItem', this.idItem.toString());
        }


        // Cidade
        if (this.idCidade) {
            params = params.append('idCidade', this.idCidade.toString());
        }
        // Estados
        this.idsEstados?.forEach(idEstado => {
            params = params.append('idEstado', idEstado.toString());
        });

        // Status dos Pedidos
        if (this.isReferenciaPedido()) {
            this.statusesPedidos?.forEach(statusPedido => params = params.append('statusPedido', statusPedido.toString()));
        } else {
            params = params.delete('statusPedido');
        }

        // Tags de Clientes
        this.idsTagsClientes?.forEach(idTagCliente => {
            params = params.append('idsTagsClientes', idTagCliente.toString());
        });

        // Tags de Itens
        this.idsTagsItens?.forEach(idTagItem => {
            params = params.append('idsTagsItens', idTagItem.toString());
        });

        return params;
    }

    clear() {
        this.isFilterDirty = false;

        this.idPessoa = null;
        this.pessoa = null;
        this.idsRepresentantes = [];
        this.somenteRepresentantesAtivos = true;
        this.idsGerentes = [];
        this.idsAtendentes = [];
        this.idsClassificacoesClientes = [];
        this.classificacaoClienteNull = false;
        this.idsClassificacoesItens = [];
        this.classificacaoItemNull = false;
        this.idItem = null;
        this.item = null;
        this.idCidade = null;
        this.cidade = null;
        this.idsEstados = [];
        this.statusesPedidos = [];
        this.idsTagsClientes = [];
        this.idsTagsItens = [];

        this.setDefaults();
    }

    /** Verifica se a referência do filtro é por Pedido */
    isReferenciaPedido(): boolean {
        return isReferenciaPedido(this.idReferencia);
    }

    /** Verifica se a referência do filtro é por Nota Fiscal */
    isReferenciaNotaFiscal(): boolean {
        return isReferenciaNotaFiscal(this.idReferencia);
    }

    private setDefaults() {
        this.setDefaultStatusesPedidos();
    }

    /** Seta os status dos pedidos padrões para filtros os pedidos */
    private setDefaultStatusesPedidos() {
        this.statusesPedidos = [];
        const statuses = getStatusesConsultaPedidos(false, true, false);
        statuses?.forEach(status => this.statusesPedidos.push(status.id));
    }

    /** Verificar se o filtro possui a periodicidade de emissão considerando a periodicidade e  data de emissão inicial */
    possuiPeriodicidadeEmissao(): boolean {
        // Periodicidade Emissão
        if (!this.periodicidadeEmissao) {
            return false;
        }
        // Data Emissão Inicial
        if (!this.dataEmissaoInicial) {
            return false;
        }

        return true;
    }
}
